<template>
	<section class="scene-signup">
		<component v-bind:is="stepComponent" v-bind:authModel="authModel" v-bind:userModel="userModel"></component>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import ViewSignupConfirmation from '@/App/View/Signup/Confirmation.vue';
	import ViewSignupCreateAccount from '@/App/View/Signup/CreateAccount.vue';
	import ViewSignupEnterPassword from '@/App/View/Signup/EnterPassword.vue';
	import ViewSignupFavoriteGames from '@/App/View/Signup/FavoriteGames.vue';
	import ViewSignupFavoritePlaces from '@/App/View/Signup/FavoritePlaces.vue';
	import ViewSignupProfilePhoto from '@/App/View/Signup/ProfilePhoto.vue';
	import { Component, Prop, Watch } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene
	 * @project ChalkySticks App
	 */
	@Component({
		components: {
			ViewSignupConfirmation,
			ViewSignupCreateAccount,
			ViewSignupEnterPassword,
			ViewSignupFavoriteGames,
			ViewSignupFavoritePlaces,
			ViewSignupProfilePhoto,
		},
	})
	export default class SceneSignup extends SceneBase {
		/**
		 * Current signup step
		 *
		 * @type string
		 */
		protected currentStep: string = this.$route.params.step || '';

		/**
		 * List of signup step components
		 *
		 * @type Record<string, string>
		 */
		protected componentMap: Record<string, string> = {
			'': 'ViewSignupCreateAccount',
			'confirmation': 'ViewSignupConfirmation',
			'enter-password': 'ViewSignupEnterPassword',
			'favorite-games': 'ViewSignupFavoriteGames',
			'favorite-places': 'ViewSignupFavoritePlaces',
			'profile-photo': 'ViewSignupProfilePhoto',
		};

		/**
		 * Returns the current step component
		 * @return string
		 */
		public get stepComponent(): string {
			return this.componentMap[this.currentStep] || 'ViewSignupCreateAccount';
		}

		/**
		 * @type ChalkySticks.Model.Authentication
		 */
		protected authModel: ChalkySticks.Model.Authentication = ChalkySticks.Factory.Authentication.model();

		/**
		 * @type ChalkySticks.Model.User
		 */
		protected userModel: ChalkySticks.Model.User = ChalkySticks.Factory.User.model();

		/**
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on('authentication:login', () => {
				this.userModel = this.$store.getters['authentication/user'];
			});
		}

		/**
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off('authentication:login');
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @return Promise<void>
		 */
		@Watch('$route')
		protected async Handle_OnRouteChange(): Promise<void> {
			this.currentStep = this.$route.params.step || '';
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.scene-signup {
		height: 100%;

		> * {
			height: 100%;
		}

		[class^='view-signup-'] {
			background-color: var(--chalky-blue-4);
			display: flex;
			flex-direction: column;
			overflow: auto;

			.stepped-overlay-header {
				padding-top: 2rem;

				h3 {
					margin-top: 1rem;
				}
			}

			.stepped-overlay-content {
				padding: 0 1rem;
			}
		}
	}
</style>
